import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import { Pressable, type PressableProps } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import {
	BylineConnectionIconStack,
	type BylineConnectionIconStackProps,
} from './BylineConnectionIconStack.tsx';

const i18n = defineMessages({
	label: {
		id: 'byline-connections.label',
		defaultMessage: 'Context',
		description: 'Label for the connections byline item shown in the page byline area',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		marginInlineEnd: token('space.050'),
		height: '24px',
		font: token('font.body.small'),
		// The byline row is `white-space: nowrap`; the top-layer tooltip renders
		// inside this subtree and inherits it, overflowing its box. CCINT-8744
		whiteSpace: 'normal',
		color: token('color.text.subtle'),
	},
	trigger: {
		display: 'inline-flex',
		alignItems: 'center',
		columnGap: token('space.050'),
		font: token('font.body.small'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		color: token('color.text.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	triggerExpanded: {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

type TriggerAppearance = 'loaded' | 'placeholder';

type BylineConnectionsWrapperProps = ComponentPropsWithoutRef<'span'> & {
	children: ReactNode;
};

export const BylineConnectionsWrapper = ({
	children,
	...htmlAttributes
}: BylineConnectionsWrapperProps): React.JSX.Element => (
	<span {...htmlAttributes} css={styles.wrapper}>
		{children}
	</span>
);

type SharedTriggerProps = Pick<
	BylineConnectionIconStackProps,
	'additionalIcons' | 'incomingLinks' | 'links' | 'minIcons' | 'relatedContentLinks'
>;

type TriggerButtonProps = Omit<
	PressableProps,
	'aria-expanded' | 'aria-hidden' | 'children' | 'isDisabled' | 'testId' | 'xcss'
> & {
	'aria-expanded': boolean;
};

type BylineConnectionsTriggerProps = SharedTriggerProps & {
	appearance: TriggerAppearance;
	buttonProps: TriggerButtonProps;
};

export const BylineConnectionsTrigger = (
	props: BylineConnectionsTriggerProps,
): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { additionalIcons, appearance, incomingLinks, links, minIcons, relatedContentLinks } =
		props;
	const content = (
		<>
			<TeamworkGraphIcon
				label=""
				size="small"
				color={token('color.icon.subtle')}
				testId="byline-connections-teamwork-graph-icon"
			/>
			<Text as="span" size="small" color="color.text.subtle">
				{formatMessage(i18n.label)}
			</Text>
			<BylineConnectionIconStack
				links={links}
				incomingLinks={incomingLinks}
				relatedContentLinks={relatedContentLinks}
				additionalIcons={additionalIcons}
				appearance={appearance === 'placeholder' ? 'placeholder' : 'default'}
				minIcons={minIcons}
				spacing="none"
			/>
		</>
	);

	return (
		<Pressable
			{...props.buttonProps}
			testId="byline-connections-trigger"
			xcss={cx(styles.trigger, props.buttonProps['aria-expanded'] && styles.triggerExpanded)}
		>
			{content}
		</Pressable>
	);
};
